import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  MapPinIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { publicSecurityStatus } from '../../security/publicSecurityStatus';

const primaryLink =
  'inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-ratuj-400 px-5 py-2.5 text-sm font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300 focus-outline';
const secondaryLink =
  'inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-primary-dark-300/75 bg-primary-dark-200/65 px-5 py-2.5 text-sm font-semibold text-primary-light-100 transition-colors hover:bg-primary-dark-300/75 focus-outline';

const SectionHeader: React.FC<{
  label: string;
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, title, children, className = '' }) => (
  <div className={`max-w-3xl ${className}`}>
    <p className="text-sm font-semibold text-ratuj-300">{label}</p>
    <h2 className="mt-3 text-2xl font-semibold leading-tight text-primary-light-100 sm:text-3xl">
      {title}
    </h2>
    <div className="mt-4 text-base leading-7 text-primary-light-300">{children}</div>
  </div>
);

export const BuildTogetherSection: React.FC<{ id: string }> = ({ id }) => {
  const steps = [
    {
      title: 'Najpierw lokalne serwisy',
      description: 'Profile i widoczność serwisów mają być pierwszym realnym fundamentem sieci.',
    },
    {
      title: 'Potem coraz lepszy proces zgłoszeń',
      description: 'Zgłoszenie, kolejka pracy i komunikacja mają porządkować codzienną obsługę.',
    },
    {
      title: 'Zmiany omawiane i głosowane z użytkownikami',
      description:
        'Sugestie serwisów pomagają ustalać, co warto rozwijać, uprościć albo zatrzymać.',
    },
  ];

  return (
    <section id={id} className="border-y border-primary-dark-300/45 bg-primary-dark-200/62">
      <div className="rs-page-gutter mx-auto grid max-w-7xl gap-10 py-14 sm:py-16 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16 lg:py-20">
        <SectionHeader
          label="Uczciwy etap budowy"
          title="Nie udajemy gotowego giganta. Budujemy narzędzie z ludźmi od napraw."
        >
          <p>
            Ratuj Sprzęt jest rozwijany krok po kroku: od profili serwisów, przez zgłoszenia napraw,
            po kolejkę pracy i decyzje produktowe. Chcemy budować narzędzie z ludźmi, którzy będą go
            używać codziennie, nie obok nich.
          </p>
        </SectionHeader>

        <ol className="space-y-5">
          {steps.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span className="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-sm font-semibold text-ratuj-200">
                {index + 1}
              </span>
              <div className="border-l border-primary-dark-300/65 pl-5">
                <h3 className="text-base font-semibold text-primary-light-100">{step.title}</h3>
                <p className="mt-1 text-sm leading-6 text-primary-light-300">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const PointList: React.FC<{ items: string[]; quiet?: boolean }> = ({ items, quiet = false }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5 text-sm leading-6 text-primary-light-200">
        <CheckCircleIcon
          className={`mt-0.5 h-5 w-5 flex-none ${quiet ? 'text-primary-light-400' : 'text-ratuj-300'}`}
          aria-hidden="true"
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const AudiencePathsSection: React.FC = () => {
  const servicePoints = [
    'profil publiczny serwisu',
    'zgłoszenia w jednym miejscu',
    'komunikacja z klientem',
    'wpływ na rozwój przez sugestie',
  ];
  const customerPoints = [
    'mapa i lista serwisów',
    'opis problemu i zdjęcia',
    'jedno miejsce do śledzenia sprawy',
  ];
  const serviceTrustCards = [
    {
      title: 'Panel tylko dla zatwierdzonych serwisów',
      description: 'Dostęp operacyjny ma być zależny od statusu konta i roli.',
    },
    {
      title: 'Prywatne dane poza widokiem publicznym',
      description:
        'Dane kontaktowe, zgłoszenia, zdjęcia i rozmowy nie mogą mieszać się z publicznym profilem.',
    },
    {
      title: 'Security Gate przed większymi zmianami',
      description:
        'Zmiany w rolach, zgłoszeniach, zdjęciach, czacie i funkcjach backendowych mają przechodzić dodatkową kontrolę.',
    },
  ];

  return (
    <section className="bg-primary-dark-100">
      <div className="rs-page-gutter mx-auto max-w-7xl py-14 sm:py-16 lg:py-20">
        <SectionHeader
          label="Dwie proste ścieżki"
          title="Serwis wchodzi wcześnie. Klient szybciej trafia do właściwego miejsca."
          className="mb-8 lg:mb-10"
        >
          <p>
            Landing prowadzi obie strony, ale priorytet jest jasny: teraz najważniejsze jest
            zaproszenie lokalnych serwisów do współtworzenia standardu.
          </p>
        </SectionHeader>

        <div className="grid gap-5 lg:grid-cols-[1.22fr_0.78fr]">
          <article
            id="for-services"
            className="rounded-lg border border-ratuj-300/28 bg-primary-dark-200/78 p-5 shadow-card sm:p-7 lg:p-8"
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-ratuj-200">
                  <WrenchScrewdriverIcon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h2 className="text-2xl font-semibold leading-tight text-primary-light-100">
                  Wejdź wcześnie jako serwis
                </h2>
                <p className="mt-4 text-base leading-7 text-primary-light-300">
                  Dodaj profil, pokaż klientom zakres napraw, odbieraj zgłoszenia i pomagaj nam
                  dopracować narzędzie, zanim stanie się sztywnym systemem bez głosu użytkowników.
                </p>
              </div>
              <Link
                to="/register/service"
                className={`${primaryLink} w-full sm:w-auto lg:flex-none`}
              >
                Dołącz jako serwis
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            <div className="mt-7 border-t border-primary-dark-300/65 pt-6">
              <PointList items={servicePoints} />
            </div>
          </article>

          <article
            id="for-customers"
            className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/48 p-5 sm:p-7 lg:p-8"
          >
            <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gold-300/25 bg-gold-300/10 text-gold-300">
              <MapPinIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-semibold leading-tight text-primary-light-100">
              Znajdź serwis bliżej siebie
            </h2>
            <p className="mt-4 text-sm leading-6 text-primary-light-300">
              Szukaj po lokalizacji, zobacz profil serwisu i zgłoś problem bez telefonowania po
              omacku.
            </p>

            <div className="mt-6">
              <PointList items={customerPoints} quiet />
            </div>

            <Link to="/map" className={`${secondaryLink} mt-7 w-full`}>
              Znajdź serwis
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </article>
        </div>

        <div className="mt-6 grid gap-6 rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/42 p-5 sm:p-7 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:p-8">
          <div>
            <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-ratuj-200">
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <SectionHeader
              label="Zaufanie i dostęp"
              title="Bezpieczeństwo budujemy razem z produktem."
            >
              <p>
                Najpierw pilnujemy fundamentów: kto ma dostęp do zgłoszeń, które dane są publiczne,
                a które prywatne, oraz czy krytyczne akcje serwisu nie opierają się tylko na widoku
                w aplikacji. Kolejne warstwy, takie jak MFA, wdrażamy etapowo.
              </p>
            </SectionHeader>
            <Link to="/bezpieczenstwo" className={`${secondaryLink} mt-7 w-full sm:w-auto`}>
              Zobacz zasady bezpieczeństwa
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {serviceTrustCards.map((item) => (
              <article
                key={item.title}
                className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-100/45 p-4"
              >
                <CheckCircleIcon className="h-5 w-5 text-ratuj-300" aria-hidden="true" />
                <h3 className="mt-3 text-sm font-semibold leading-snug text-primary-light-100">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-primary-light-300">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const TrustSecuritySection: React.FC = () => (
  <section className="border-t border-primary-dark-300/45 bg-primary-dark-100">
    <div className="rs-page-gutter mx-auto max-w-7xl py-14 sm:py-16 lg:py-20">
      <div className="grid gap-8 lg:grid-cols-[0.92fr_0.58fr] lg:items-start lg:gap-12">
        <div>
          <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 text-ratuj-200">
            <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <SectionHeader
            label="Zaufanie i bezpieczeństwo"
            title="Bezpieczny rozwój bez pustych obietnic."
          >
            <p>
              Ratuj Sprzęt jest budowany etapowo, dlatego pokazujemy nie tylko funkcje, ale też
              sposób kontroli zmian. Dla obszarów takich jak logowanie, role, zgłoszenia, zdjęcia i
              komunikacja stosujemy wewnętrzną bramkę bezpieczeństwa i jakości kodu.
            </p>
          </SectionHeader>
        </div>

        <aside className="rounded-lg border border-ratuj-300/28 bg-primary-dark-200/72 p-5 shadow-card sm:p-6">
          <p className="text-sm font-semibold text-primary-light-100">Status kontroli</p>
          <p className="mt-4 text-2xl font-semibold leading-tight text-ratuj-200">
            {publicSecurityStatus.shortLabel}
          </p>
          <p className="mt-3 text-sm leading-6 text-primary-light-300">
            Publicznie pokazujemy zakres i status procesu. Szczegółowe raporty techniczne zostają
            prywatne, żeby nie ułatwiać ataków.
          </p>
        </aside>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {[
          {
            title: 'Role i dostęp',
            description:
              'Klient, serwis i administrator mają mieć oddzielne uprawnienia. Dostęp nie może opierać się wyłącznie na tym, co widać w interfejsie.',
          },
          {
            title: 'Zgłoszenia, zdjęcia i czat',
            description:
              'Prywatne dane, załączniki i rozmowy nie powinny być widoczne dla niepowiązanych kont ani publicznych odwiedzających.',
          },
          {
            title: 'Security Gate dla zmian',
            description:
              'Większe zmiany są sprawdzane pod kątem reguł Firebase, Cloud Functions, przepływu zleceń i jakości kodu.',
          },
        ].map((item) => (
          <article
            key={item.title}
            className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/50 p-5"
          >
            <CheckCircleIcon className="h-5 w-5 text-ratuj-300" aria-hidden="true" />
            <h3 className="mt-4 text-base font-semibold leading-snug text-primary-light-100">
              {item.title}
            </h3>
            <p className="mt-3 text-sm leading-6 text-primary-light-300">{item.description}</p>
          </article>
        ))}
      </div>

      <Link to="/bezpieczenstwo" className={`${secondaryLink} mt-8 w-full sm:w-auto`}>
        Zobacz podejście do bezpieczeństwa
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </section>
);

const DecisionMock: React.FC = () => (
  <div className="rounded-lg border border-primary-dark-300/65 bg-primary-dark-200/82 p-5 shadow-card sm:p-6">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-gold-300">Przykład decyzji produktowej</p>
        <h3 className="mt-2 text-lg font-semibold leading-tight text-primary-light-100">
          Czy uprościć kartę zlecenia?
        </h3>
      </div>
      <span className="inline-flex w-fit rounded-md border border-ratuj-300/25 bg-ratuj-300/10 px-2.5 py-1 text-xs font-semibold text-ratuj-200">
        Głosowanie serwisów
      </span>
    </div>

    <div className="mt-6 grid gap-3 sm:grid-cols-2" aria-label="Przykładowe opcje głosowania">
      <div className="rounded-lg border border-ratuj-300/25 bg-ratuj-300/10 p-4">
        <p className="text-xs text-primary-light-400">Opcja</p>
        <p className="mt-1 text-xl font-semibold text-ratuj-200">TAK</p>
      </div>
      <div className="rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/55 p-4">
        <p className="text-xs text-primary-light-400">Opcja</p>
        <p className="mt-1 text-xl font-semibold text-primary-light-200">NIE</p>
      </div>
    </div>

    <p className="mt-5 text-xs leading-5 text-primary-light-400">
      To komunikacyjny przykład mechanizmu, nie realny rekord ani wynik. Sugestie są częścią panelu
      po zalogowaniu.
    </p>
  </div>
);

export const CoCreationSection: React.FC<{ id: string }> = ({ id }) => (
  <section id={id} className="border-y border-primary-dark-300/45 bg-primary-dark-200/62">
    <div className="rs-page-gutter mx-auto grid max-w-7xl gap-10 py-14 sm:py-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14 lg:py-20">
      <div>
        <SectionHeader label="Głos serwisów" title="To nie ma być platforma narzucona serwisom.">
          <p>
            W Ratuj Sprzęt serwisy mogą zgłaszać pomysły, głosować nad zmianami i zatrzymywać
            rozwiązania, które nie mają sensu w codziennej pracy. Chcemy, żeby zasady produktu
            wynikały z praktyki, nie z prezentacji.
          </p>
        </SectionHeader>

        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          <Link to="/register/service" className={`${primaryLink} w-full sm:w-auto`}>
            Chcę współtworzyć platformę
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>

      <DecisionMock />
    </div>
  </section>
);

export const FinalInviteSection: React.FC<{ id: string }> = ({ id }) => (
  <section id={id} className="bg-primary-dark-100">
    <div className="rs-page-gutter mx-auto max-w-7xl py-14 sm:py-16 lg:py-20">
      <div className="rounded-lg border border-primary-dark-300/62 bg-primary-dark-200/72 p-5 shadow-card sm:p-8 lg:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-ratuj-300">Zaproszenie</p>
            <h2 className="mt-3 text-2xl font-semibold leading-tight text-primary-light-100 sm:text-3xl">
              Jeśli prowadzisz serwis, Twój głos może mieć znaczenie teraz.
            </h2>
            <p className="mt-4 text-base leading-7 text-primary-light-300">
              Jeśli masz dość narzędzi projektowanych bez rozmowy z ludźmi od napraw, dołącz teraz.
              Ten produkt jeszcze powstaje. I właśnie dlatego Twój głos może mieć znaczenie.
            </p>
            <p className="mt-6 text-sm text-primary-light-400">- Ratuj Sprzęt</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
            <Link to="/register/service" className={`${primaryLink} w-full sm:w-auto`}>
              Dołącz jako serwis
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a href="mailto:kontakt@ratujsprzet.pl" className={`${secondaryLink} w-full sm:w-auto`}>
              Napisz do nas
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);
